import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Eye, EyeOff, ArrowRight, MapPin, PackagePlus, Mail, Lock } from "lucide-react";
import { toast } from "@/lib/toast";
import { triggerHaptic } from "@/lib/haptics";

export default function Login() {
const { user, login, formatApiErrorDetail } = useAuth();
const navigate = useNavigate();
const [email, setEmail] = useState("");
const [password, setPassword] = useState("");
const [showPw, setShowPw] = useState(false);
const [error, setError] = useState("");
const [loading, setLoading] = useState(false);

useEffect(() => {
if (user && user !== null) navigate("/dashboard");
}, [user, navigate]);

const handleSubmit = async (e) => {
e.preventDefault();
setError("");
setLoading(true);
try {
await login(email, password);
toast.success("Welcome back!");
navigate("/dashboard");
} catch (err) {
triggerHaptic("error");
setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
} finally {
setLoading(false);
}
};

return (
<AuthLayout
title="Sign In"
subtitle="Access your logistics dashboard"
footer={
<>
No account?{" "}
<Link to="/register" className="font-semibold text-primary hover:underline" data-testid="go-register-link">
Create one
</Link>
</>
}
>
<form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
<div className="space-y-2">
<Label htmlFor="email">Email</Label>
<div className="relative">
<Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
<Input id="email" type="email" className="pl-10" value={email} onChange={(e) => setEmail(e.target.value)}
placeholder="you@company.com" required autoComplete="email" data-testid="login-email-input" />
</div>
</div>
<div className="space-y-2">
<div className="flex items-center justify-between">
<Label htmlFor="password">Password</Label>
<Link to="/forgot-password" className="text-xs text-muted-foreground transition-colors hover:text-primary" data-testid="forgot-password-link">
Forgot password?
</Link>
</div>
<div className="relative">
<Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
<Input id="password" type={showPw ? "text" : "password"} className="pl-10 pr-11" value={password} onChange={(e) => setPassword(e.target.value)}
placeholder="••••••••" required autoComplete="current-password" data-testid="login-password-input" />
<button type="button" onClick={() => { triggerHaptic("tap"); setShowPw((s) => !s); }} data-testid="toggle-password-visibility" aria-label={showPw ? "Hide password" : "Show password"}
className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
</button>
</div>
</div>
{error && (
<p className="shake flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">
{error}
</p>
)}
<Button type="submit" size="lg" className="w-full shine-hover" disabled={loading} data-testid="login-submit-button">
{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign In <ArrowRight className="h-4 w-4" /></>}
</Button>
</form>

<div className="mt-6 grid grid-cols-2 gap-2">
<Link to="/track" data-testid="public-track-link" onClick={() => triggerHaptic("tap")}
className="group flex items-center gap-2 rounded-xl border border-border bg-white/70 px-3 py-2.5 text-sm font-medium transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary">
<MapPin className="h-4 w-4 text-primary" /> Track a shipment
</Link>
<Link to="/book" data-testid="guest-book-link" onClick={() => triggerHaptic("tap")}
className="group flex items-center gap-2 rounded-xl border border-border bg-white/70 px-3 py-2.5 text-sm font-medium transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary">
<PackagePlus className="h-4 w-4 text-primary" /> Book as guest
</Link>
</div>
</AuthLayout>
);
}
